import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { screenModeConfig } from '@/config/screen-mode-config';

type ActiveMode = 'demo' | 'live';

interface ScreenModesContextType {
  /** Full record of screen key → active mode */
  screenModes: Record<string, ActiveMode>;
  /** Get the effective mode for a given screen key */
  getScreenMode: (screenKey: string) => ActiveMode;
  /** Set the mode for a specific screen key (only works for 'all' screens) */
  setScreenMode: (screenKey: string, mode: ActiveMode) => void;
  /** Set all toggleable ('all') screens to demo */
  setAllToDemo: () => void;
  /** Set all toggleable ('all') screens to live */
  setAllToLive: () => void;
}

const STORAGE_KEY = 'screen-data-modes';

const ScreenModesContext = createContext<ScreenModesContextType | undefined>(undefined);

const defaultLiveScreens = new Set(['Settings_Dispute_Types']);

function getDefaultModes(): Record<string, ActiveMode> {
  const defaults: Record<string, ActiveMode> = {};
  for (const key of Object.keys(screenModeConfig)) {
    if (screenModeConfig[key] === 'all' && defaultLiveScreens.has(key)) {
      defaults[key] = 'live';
    } else {
      defaults[key] = 'demo';
    }
  }
  return defaults;
}

function loadPersistedModes(): Record<string, ActiveMode> {
  const defaults = getDefaultModes();
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaults;
    const parsed = JSON.parse(stored) as Record<string, string>;
    // Merge stored values into defaults, validating each entry
    for (const [key, value] of Object.entries(parsed)) {
      if (key in defaults && (value === 'demo' || value === 'live')) {
        // Only allow 'live' for screens that are 'all' (toggleable)
        const configMode = screenModeConfig[key];
        if (configMode === 'all') {
          defaults[key] = value as ActiveMode;
        }
        // 'demo'-only screens stay 'demo' regardless of stored value
      }
    }
    return defaults;
  } catch {
    return defaults;
  }
}

interface ScreenModesProviderProps {
  children: ReactNode;
}

export function ScreenModesProvider({ children }: Readonly<ScreenModesProviderProps>) {
  const [screenModes, setScreenModes] = useState<Record<string, ActiveMode>>(loadPersistedModes);

  // Persist to localStorage on changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(screenModes));
    } catch (error) {
      console.error('Failed to persist screen modes:', error);
    }
  }, [screenModes]);

  const getScreenMode = useCallback(
    (screenKey: string): ActiveMode => {
      return screenModes[screenKey] ?? 'demo';
    },
    [screenModes]
  );

  const setScreenMode = useCallback((screenKey: string, mode: ActiveMode) => {
    const configMode = screenModeConfig[screenKey];
    // Only allow toggling for 'all' screens
    if (configMode !== 'all') return;
    setScreenModes((prev) => ({ ...prev, [screenKey]: mode }));
  }, []);

  const setAllToDemo = useCallback(() => {
    setScreenModes((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        const configMode = screenModeConfig[key];
        if (configMode === 'all') {
          next[key] = 'demo';
        }
      }
      return next;
    });
  }, []);

  const setAllToLive = useCallback(() => {
    setScreenModes((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        const configMode = screenModeConfig[key];
        if (configMode === 'all') {
          next[key] = 'live';
        }
      }
      return next;
    });
  }, []);

  const value = useMemo<ScreenModesContextType>(
    () => ({
      screenModes,
      getScreenMode,
      setScreenMode,
      setAllToDemo,
      setAllToLive,
    }),
    [screenModes, getScreenMode, setScreenMode, setAllToDemo, setAllToLive]
  );

  return <ScreenModesContext.Provider value={value}>{children}</ScreenModesContext.Provider>;
}

export function useScreenModes() {
  const context = useContext(ScreenModesContext);
  if (context === undefined) {
    throw new Error('useScreenModes must be used within a ScreenModesProvider');
  }
  return context;
}
